import { Alert, Input, InputNumber, Slider, Tooltip } from "antd";
import {
  InfoCircleOutlined,
  ThunderboltOutlined,
  BuildOutlined,
} from "@ant-design/icons";
import type { AiConnection, AiProfile } from "@/lib/api/resources/agents";
import type { AgentModelCatalogEntry } from "@/lib/api/resources/benchmark";
import { InferenceOptionsOverrideEditor } from "@/components/ai-connection/InferenceOptionsOverrideEditor";
import {
  getOverrideOption,
  setOverrideOption,
} from "@/components/ai-connection/inferenceOptionsOverride";

/**
 * Per-run inference overrides. A blank/cleared field keeps the agent's own value; set a value
 * to test how it affects quality and speed. Generation parameters (temperature, top_p, ...) live in
 * {@link override}.options (snake_case); the model, provider, and credentials come from the selected
 * AI Profile and can no longer be overridden. Only non-connection agent settings (instructions, max tokens)
 * are dedicated fields here.
 */
export interface ParameterValues {
  override?: Record<string, any> | null;
  aiProfileId: string | null;
  instructions: string;
  maxTokensPerSession: number | null;
  limit: number | null;
}

export const EMPTY_PARAMETERS: ParameterValues = {
  override: null,
  aiProfileId: null,
  instructions: "",
  maxTokensPerSession: null,
  limit: 1,
};

const toNumber = (value: number | string | null): number | null =>
  value === null || value === "" ? null : Number(value);

const SliderField = ({
  label,
  value,
  onChange,
  min,
  max,
  step,
  tooltipTitle,
  description,
  initialValue,
}: any) => (
  <div className="mb-4 bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
    <div className="flex items-center gap-2 mb-1.5">
      <label className="block text-sm font-semibold text-gray-800">
        {label}
      </label>
      <Tooltip
        title={tooltipTitle}
        color="blue"
        styles={{ root: { width: "300px" } }}
      >
        <InfoCircleOutlined className="text-blue-500 hover:text-blue-700 cursor-help" />
      </Tooltip>
    </div>
    <p className="text-xs text-gray-500 mb-4 leading-relaxed">{description}</p>
    <div className="flex items-center gap-5">
      <Slider
        min={min}
        max={max}
        step={step}
        value={typeof value === "number" ? value : (initialValue ?? min)}
        onChange={(v) => onChange(v)}
        className="flex-grow m-0"
      />
      <InputNumber
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(v) => onChange(toNumber(v))}
        className="w-20 bg-gray-50 border-gray-200 shadow-inner rounded-md font-mono text-center"
      />
    </div>
  </div>
);

interface Props {
  values: ParameterValues;
  onChange: (patch: Partial<ParameterValues>) => void;
  aiConnection?: AiConnection | null;
  modelCatalog?: AgentModelCatalogEntry[];
  agentProfile?: AiProfile | null;
}

export const BenchmarkParametersStep = ({
  values,
  onChange,
  agentProfile,
}: Props) => {
  const getOption = (key: string) => getOverrideOption(values.override, key);
  const setOption = (key: string, v: number | null) =>
    onChange({ override: setOverrideOption(values.override, key, v) });

  return (
    <div className="space-y-4">
      <p className="text-gray-500 text-sm">
        These override the agent's settings for this run only — leave a field
        unchanged to keep the agent's default. Vary them across runs to compare
        quality and speed.
      </p>

      {agentProfile && (
        <Alert
          message={
            <span className="font-semibold text-gray-800">
              {agentProfile.name} Parameters
            </span>
          }
          description={
            <div className="mt-2 text-sm text-gray-600 flex flex-col gap-2">
              {(agentProfile.contextWindowInput ||
                agentProfile.contextWindowOutput) && (
                <div className="flex items-center gap-2">
                  <ThunderboltOutlined className="text-amber-500" />
                  <span>
                    <strong>Context Windows:</strong>{" "}
                    {agentProfile.contextWindowInput
                      ? `${agentProfile.contextWindowInput.toLocaleString()} Input`
                      : "Unknown Input"}{" "}
                    /{" "}
                    {agentProfile.contextWindowOutput
                      ? `${agentProfile.contextWindowOutput.toLocaleString()} Output`
                      : "Unknown Output"}
                  </span>
                </div>
              )}
              {agentProfile.capabilities &&
                agentProfile.capabilities.length > 0 && (
                  <div className="flex items-center gap-2">
                    <BuildOutlined className="text-blue-500" />
                    <span>
                      <strong>Capabilities:</strong>{" "}
                      {agentProfile.capabilities.join(", ")}
                    </span>
                  </div>
                )}
            </div>
          }
          type="info"
          showIcon
          className="bg-blue-50/50 border-blue-100"
        />
      )}

      <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <InferenceOptionsOverrideEditor
          label="Agent AI Profile & Inference Overrides"
          help="Select an AI Profile, or override inference options (e.g. temperature). The model and credentials always come from the selected profile. If blank, the agent's configured profile will be used."
          value={values.override}
          profileId={values.aiProfileId}
          onChangeOverride={(override) => onChange({ override })}
          onChangeProfile={(aiProfileId) => onChange({ aiProfileId })}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-1.5">
            <label className="block text-sm font-semibold text-gray-800">
              Max tokens
            </label>
            <Tooltip
              title="The maximum number of tokens to generate in the completion."
              color="blue"
            >
              <InfoCircleOutlined className="text-blue-500 hover:text-blue-700 cursor-help" />
            </Tooltip>
          </div>
          <p className="text-xs text-gray-500 mb-4 leading-relaxed">
            Roughly 75 words per 100 tokens. Leave blank for agent default.
          </p>
          <InputNumber
            className="w-full bg-gray-50 border-gray-200 shadow-inner rounded-md font-mono"
            min={1}
            precision={0}
            value={values.maxTokensPerSession}
            onChange={(v) => onChange({ maxTokensPerSession: toNumber(v) })}
            placeholder="e.g. 50"
          />
        </div>

        <SliderField
          label="Temperature"
          value={getOption("temperature")}
          onChange={(v: number | null) => setOption("temperature", v)}
          min={0}
          max={1}
          step={0.1}
          initialValue={0.0}
          tooltipTitle="Controls the randomness of the model. Lower values make the model more focused and deterministic, while higher values make it more creative and random."
          description="0.0 is strictly factual (good for data extraction). 1.0 allows maximum creativity (good for brainstorming)."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Instructions (system prompt)
        </label>
        <Input.TextArea
          rows={4}
          value={values.instructions}
          onChange={(e) => onChange({ instructions: e.target.value })}
        />
      </div>

      <details className="rounded-lg border border-gray-200 p-4 bg-gray-50">
        <summary className="cursor-pointer text-sm font-semibold text-gray-700 select-none">
          Advanced Sampling Options
        </summary>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <SliderField
            label="Top‑P (Nucleus Sampling)"
            value={getOption("top_p")}
            onChange={(v: number | null) => setOption("top_p", v)}
            min={0}
            max={1}
            step={0.05}
            initialValue={0.9}
            tooltipTitle="Determines the pool of words considered by the model. A value of 0.6 means it only considers words comprising the top 60% of probability mass."
            description="0.6 = highly predictable (filters out unlikely words). 0.9 = good balance. 1.0 = highly creative (considers all words)."
          />
        </div>
      </details>
    </div>
  );
};
